/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { CustomError } from "@Internal/Models";
import { Autobind } from "@Internal/Utils";

import {
    Newsletter, NewsletterBlog, NewsletterContact, NewsletterCount, NewsletterFields,
    NewsletterPostStats, NewsletterPreview, NewsletterSearch, NewsletterSend,
    NewsletterOffer, NewsletterSendRecipients, NewsletterSendingDomain, NewsletterSummary,
    NewsletterTag,
    NEWSLETTER_FEATURE
} from "App/Models/Newsletter";

import { ProcessException } from "./Base";

type Result<T> = Promise<[ T | undefined, CustomError | undefined ]>;

type Method = "GET" | "POST" | "PATCH" | "DELETE";

/* LA NEWSLETTER AUTOMATIQUE D'UN CLIENT, depuis la Console ( Fred, 21/10/2026 ).

   Tout passe par le relais `Internal/Newsletters` du back de la Console, qui
   confronte chaque appel a la liste des routes `console/*` permises et y ajoute la
   clef. Les reponses de l'API arrivent telles quelles. Une erreur revient en
   `{ Id, Message, SubId }` : `Id` = code HTTP, `Message` = code de l'API
   ( `no_dynamic_block`, `contact_not_found`… ), `SubId` = « champ : raison ». */
export default class NewslettersService {
    private service: Feathers.Service<any>;

    constructor( service: Feathers.Service<any> ) {
        this.service = service;
    }

    /* Les clients qui ont l'offre. Une erreur « introuvable » est rendue telle
       quelle : l'ecran distingue le relais absent du back de la Console ( service
       `Internal/Newsletters` inconnu ) de la route absente de l'API. */
    @Autobind
    async Offers( enabled?: boolean ): Result<NewsletterOffer[]> {
        const [ result, error ] = await this.call( "GET", "features/newsletter", {
            enabled: ( enabled === undefined ) ? undefined : String( enabled )
        } );

        if( result === undefined || error !== undefined )
            return [ undefined, error ];

        return [ asArray( result.items ), undefined ];
    }

    @Autobind
    async Enabled( groupId: number ): Result<boolean> {
        const [ result, error ] = await this.call( "GET", `group/${ groupId }/features` );

        if( result === undefined )
            return [ undefined, error ];

        return [ result[ NEWSLETTER_FEATURE ] === true, undefined ];
    }

    @Autobind
    async SetEnabled( groupId: number, enabled: boolean ): Result<boolean> {
        const [ result, error ] = await this.call(
            "PATCH", `group/${ groupId }/features/update`, {},
            { feature: NEWSLETTER_FEATURE, enabled: enabled ? "true" : "false" }
        );

        if( result === undefined )
            return [ undefined, error ];

        return [ result[ NEWSLETTER_FEATURE ] === true, undefined ];
    }

    @Autobind
    async List( groupId: number ): Result<Newsletter[]> {
        const [ result, error ] = await this.call( "GET", "newsletter/list", { groupId } );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async Read( groupId: number, id: number ): Result<Newsletter> {
        return this.call( "GET", `newsletter/${ id }`, { groupId } );
    }

    @Autobind
    async Create( groupId: number, fields: NewsletterFields ): Result<Newsletter> {
        return this.call( "POST", "newsletter/create", {}, { groupId, ...form( fields ) } );
    }

    @Autobind
    async Update( groupId: number, id: number, fields: NewsletterFields ): Result<Newsletter> {
        return this.call(
            "PATCH", `newsletter/${ id }/update`, {}, { groupId, ...form( fields ) }
        );
    }

    @Autobind
    async Delete( groupId: number, id: number ): Promise<CustomError | undefined> {
        const [ , error ] = await this.call( "DELETE", `newsletter/${ id }/delete`, { groupId } );

        return error;
    }

    @Autobind
    async Count( groupId: number, search: NewsletterSearch ): Result<NewsletterCount> {
        return this.call(
            "GET", "recipients/count", { groupId, search: JSON.stringify( search ) }
        );
    }

    @Autobind
    async Preview( groupId: number, id: number, contactId?: number ): Result<NewsletterPreview> {
        return this.call( "GET", `newsletter/${ id }/preview`, { groupId, contactId } );
    }

    @Autobind
    async Test( groupId: number, id: number, email: string, contactId?: number ): Result<any> {
        return this.call( "POST", `newsletter/${ id }/test`, {}, {
            groupId,
            email,
            contact_id: contactId
        } );
    }

    @Autobind
    async Stats(
        groupId: number,
        id: number,
        from?: string,
        to?: string
    ): Result<NewsletterSummary> {
        return this.call( "GET", `newsletter/${ id }/stats`, { groupId, from, to } );
    }

    @Autobind
    async Sends( groupId: number, id: number ): Result<NewsletterSend[]> {
        const [ result, error ] = await this.call( "GET", `newsletter/${ id }/sends`, { groupId } );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async SendRecipients(
        groupId: number,
        id: number,
        sendId: number,
        page: number = 1,
        limit: number = 50
    ): Result<NewsletterSendRecipients> {
        return this.call(
            "GET", `newsletter/${ id }/sends/${ sendId }/recipients`, { groupId, page, limit }
        );
    }

    @Autobind
    async PostsStats(
        groupId: number,
        id: number,
        from?: string,
        to?: string
    ): Result<NewsletterPostStats[]> {
        const [ result, error ] = await this.call(
            "GET", `newsletter/${ id }/posts-stats`, { groupId, from, to }
        );

        if( result === undefined )
            return [ undefined, error ];

        /* ⚠️ Le back rend `{ period, posts }`, pas `items` ( 28/10/2026 : classement vide ). */
        return [
            Array.isArray( result ) ? result : asArray( result.posts || result.items ),
            undefined
        ];
    }

    @Autobind
    async SendingDomains( groupId: number ): Result<NewsletterSendingDomain[]> {
        const [ result, error ] = await this.call( "GET", `group/${ groupId }/sending-domains` );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async Tags( groupId: number, search: string = "" ): Result<NewsletterTag[]> {
        const [ result, error ] = await this.call( "GET", `group/${ groupId }/tags`, { search } );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async Contacts(
        groupId: number,
        search: string,
        limit: number = 20
    ): Result<NewsletterContact[]> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/contacts`, { search, page: 1, limit }
        );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async Blogs( groupId: number ): Result<NewsletterBlog[]> {
        const [ result, error ] = await this.call( "GET", `group/${ groupId }/blogs` );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    /* Les articles d'un blog du client ( `{ items: [{ id, title, url, picture, status,
       published_at… }] }`, reponse back du 21/10/2026 ). Sert au mode demo des
       statistiques. */
    @Autobind
    async BlogPosts(
        groupId: number,
        blogId: number,
        limit: number = 40
    ): Result<{ id: number; title: string; url: string; status: string }[]> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/blogs/${ blogId }/posts`, { page: 1, limit }
        );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    /* Modifier, relire et creer un blog du client ( brief du 26/10/2026 : routes a
       venir ). Corps url-encode, comme cote client ( `blog-rss/*` ). */
    @Autobind
    async BlogUpdate(
        groupId: number,
        blogId: number,
        fields: { name?: string; urlFeed?: string; startDate?: string; status?: string }
    ): Result<NewsletterBlog> {
        return this.call( "PATCH", `group/${ groupId }/blogs/${ blogId }/update`, {}, fields );
    }

    /* Creer un blog pour le client ( reponse back du 26/10/2026, § 2 ) : au nom du
       proprietaire du compte, l'import part comme cote client. 201 : le blog cree. */
    @Autobind
    async BlogCreate(
        groupId: number,
        fields: { name: string; urlFeed: string; startDate: string }
    ): Result<NewsletterBlog> {
        return this.call( "POST", `group/${ groupId }/blogs/create`, {}, fields );
    }

    /* CE QUE LE PB E-MAIL LIT DANS LE COMPTE DU CLIENT ( brief du 26/10/2026, routes a
       venir ). Rendus bruts : l'ecran d'edition les met en forme, et lit une route
       absente comme « rien » ( l'editeur s'ouvre quand meme ). */
    @Autobind
    async PublicMedias( groupId: number, page: number = 1, limit: number = 40 ): Result<any[]> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/public-medias`, { page, limit }
        );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    /* LA MEDIATHEQUE DU CLIENT, AU DESSIN DU DESKTOP ( Fred, 27/10/2026 : lister,
       chercher, ajouter, renommer — jamais supprimer ). Lit les deux formes de reponse :
       celle de la route Console livree le 26/10 ( `items`, `url`, `file_name`, `total` )
       et celle de `public-medias/list` du Desktop ( `_embedded.items`, `link`, `name` ),
       pour que le back puisse s'aligner sur l'une ou l'autre sans recoder ici. */
    @Autobind
    async MediaPage(
        groupId: number, page: number, limit: number, name: string
    ): Result<{ items: ConsoleMedia[]; total: number; read: number }> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/public-medias`, { page, limit, name, orderBy: "DESC" }
        );

        if( result === undefined )
            return [ undefined, error ];

        const rows = asArray<any>(
            result.items !== undefined ? result.items
                : ( result._embedded !== undefined ? result._embedded.items : [] )
        );
        const total = Number( result.total !== undefined ? result.total : result.totalItems ) || 0;

        /* Images seules ( Fred, 28/10/2026 ) : la mediatheque du client range aussi les
           pieces jointes de la messagerie ( PDF… ), inutilisables dans un e-mail. La
           route ne filtre pas par type et ne le rend pas : on juge sur l'extension.
           `read` = lignes lues, PDF compris, pour savoir s'il en reste a lire. */
        const items = rows.map( ToMedia )
            .filter( ( media: ConsoleMedia ): boolean =>
                media.url !== "" && IsEmailImage( media ) );

        return [ { items, total, read: rows.length }, error ];
    }

    /* `image` : l'image en `data:` URL, remise en fichier par le relais de la Console. */
    @Autobind
    async MediaCreate( groupId: number, image: string, name: string ): Result<ConsoleMedia> {
        const [ result, error ] = await this.call(
            "POST", `group/${ groupId }/public-medias/create`, {}, { image, name }
        );

        return [ ( result !== undefined ) ? ToMedia( result ) : undefined, error ];
    }

    @Autobind
    async MediaRename( groupId: number, id: number, name: string ): Result<ConsoleMedia> {
        const [ result, error ] = await this.call(
            "PATCH", `group/${ groupId }/public-medias/${ id }/update`, {}, { name }
        );

        return [ ( result !== undefined ) ? ToMedia( result ) : undefined, error ];
    }

    @Autobind
    async Listings( groupId: number, kind: string, limit: number ): Result<any[]> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/listings`, { kind, limit }
        );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async FunnelPages( groupId: number ): Result<any[]> {
        const [ result, error ] = await this.call( "GET", `group/${ groupId }/funnel-pages` );

        return [ ( result !== undefined ) ? asArray( result.items ) : undefined, error ];
    }

    @Autobind
    async EmailSettings( groupId: number ): Result<any> {
        return this.call( "GET", `group/${ groupId }/email-settings` );
    }

    /* Supprimer un blog du client : le blog ET tous ses articles, definitif ( reponse back
       du 26/10/2026 ). L'ecran pose deux confirmations avant. */
    @Autobind
    async BlogDelete( groupId: number, blogId: number ): Result<any> {
        return this.call( "DELETE", `group/${ groupId }/blogs/${ blogId }/delete` );
    }

    /* Le nombre d'articles d'un blog ( `total` de la liste ), pour la premiere confirmation
       de la suppression. */
    @Autobind
    async BlogPostsCount( groupId: number, blogId: number ): Result<number> {
        const [ result, error ] = await this.call(
            "GET", `group/${ groupId }/blogs/${ blogId }/posts`, { page: 1, limit: 1 }
        );

        return [ ( result !== undefined ) ? Number( result.total ) : undefined, error ];
    }

    @Autobind
    async BlogRefresh( groupId: number, blogId: number ): Result<any> {
        return this.call( "POST", `group/${ groupId }/blogs/${ blogId }/refresh`, {}, {} );
    }

    private async call(
        method: Method,
        path: string,
        query: { [ key: string ]: unknown } = {},
        formData?: { [ key: string ]: unknown }
    ): Result<any> {
        try {
            const result = await this.service.create( {
                Method: method,
                Path: path,
                Query: clean( query ),
                Form: ( formData !== undefined ) ? clean( formData ) : undefined
            } );

            const failed = result !== undefined && result !== null &&
                result.Id !== undefined && result.Message !== undefined;

            if( failed )
                return [ undefined, new CustomError( result.Id, result.Message, result.SubId ) ];

            return [ ( result === undefined || result === null ) ? {} : result, undefined ];
        } catch( error ) {
            return [ undefined, await ProcessException( error ) ];
        }
    }
}

export interface ConsoleMedia {
    id: number;
    url: string;
    name: string;
}

/* Une image de la mediatheque, quelle que soit la forme de la reponse. L'adresse doit
   etre COMPLETE ( `url`, comme la route Console du 26/10 ) : un `link` relatif de la forme
   Desktop n'a pas d'adresse de base cote Console — demande au brief. */
function ToMedia( raw: any ): ConsoleMedia {
    const row = ( raw !== null && typeof raw === "object" ) ? raw : {};

    return {
        id: Number( row.id ) || 0,
        url: String( row.url || row.link || "" ),
        /* `file_name` est le nom a AFFICHER ( le nom donne, sinon celui du fichier sans
           son horodatage ) ; `name` est `null` pour une image sans nom ( back, 28/10 ). */
        name: String( row.file_name || row.name || "" )
    };
}

/* LA MEME REGLE QUE LE DESKTOP ( `IsImage` de `App/UseCases/Media/Public/Data.ts`,
   Fred, 28/10/2026 ) : l'extension de l'adresse. En changer une = changer l'autre. */
function IsEmailImage( media: ConsoleMedia ): boolean {
    return /\.(jpe?g|png|gif|webp|svg|bmp|avif)(?:[?#].*)?$/i.test( media.url );
}

function asArray<T>( value: unknown ): T[] {
    return Array.isArray( value ) ? value as T[] : [];
}

/* Les valeurs absentes ne partent pas ; une chaine vide part ( on efface ). */
function clean( values: { [ key: string ]: unknown } ): { [ key: string ]: string } {
    const cleaned: { [ key: string ]: string } = {};

    Object.keys( values ).forEach( ( name: string ): void => {
        const value = values[ name ];

        if( value === undefined || value === null )
            return;

        cleaned[ name ] = ( typeof value === "object" ) ? JSON.stringify( value ) : String( value );
    } );

    return cleaned;
}

/* Les champs d'une newsletter tels que l'API les attend : `search` et
   `frequency` en JSON dans le formulaire. */
function form( fields: NewsletterFields ): { [ key: string ]: unknown } {
    const values: { [ key: string ]: unknown } = { ...fields };

    if( fields.active !== undefined )
        values.active = fields.active ? "true" : "false";

    return values;
}
